import React from 'react';

export type ExpenseNature = 'routine' | 'stock' | 'payroll' | 'capital' | 'admin-finance';

interface NatureTabsProps {
  businessType: 'mill' | 'vehicle';
  activeNature: ExpenseNature;
  onNatureChange: (nature: ExpenseNature) => void;
  className?: string;
}

/**
 * Level 2 Navigation: Expense Nature Selection
 * Shown only for Mill (Vehicle has only Routine nature)
 * Uses horizontal underlined tabs with monogram icons matching sidebar style
 */
const NatureTabs: React.FC<NatureTabsProps> = ({
  businessType,
  activeNature,
  onNatureChange,
  className = ''
}) => {
  // Define available natures per business type (only 5 natures)
  const millNatures: { value: ExpenseNature; label: string; monogram: string }[] = [
    { value: 'routine', label: 'Routine', monogram: 'R' },
    { value: 'stock', label: 'Stock', monogram: 'S' },
    { value: 'payroll', label: 'Payroll', monogram: 'P' },
    { value: 'capital', label: 'Capital', monogram: 'C' },
    { value: 'admin-finance', label: 'Admin-Finance', monogram: 'A' }
  ];

  const vehicleNatures: { value: ExpenseNature; label: string; monogram: string }[] = [
    { value: 'routine', label: 'Routine', monogram: 'R' }
  ];

  const availableNatures = businessType === 'mill' ? millNatures : vehicleNatures;

  // If Vehicle, don't show tabs (only 1 nature)
  if (businessType === 'vehicle') {
    return null;
  }

  return (
    <div className={`mb-4 ${className}`}>
      {/* Horizontal Underlined Tabs */}
      <div className="border-b border-border-default">
        <nav className="flex -mb-px space-x-6" aria-label="Nature tabs">
          {availableNatures.map((nature) => {
            const isActive = activeNature === nature.value;
            
            return (
              <button
                key={nature.value}
                type="button"
                onClick={() => onNatureChange(nature.value)}
                className={`
                  group inline-flex items-center gap-2 py-2.5 px-1 border-b-2 
                  font-medium text-sm transition-colors
                  ${isActive
                    ? 'border-ds-primary-500 text-ds-primary-600'
                    : 'border-transparent text-text-muted hover:text-text-secondary hover:border-border-muted'
                  }
                  focus:outline-none focus:ring-2 focus:ring-ds-primary-500 focus:ring-offset-2
                `}
                aria-selected={isActive}
                role="tab"
                aria-current={isActive ? 'page' : undefined}
              >
                {/* Monogram Icon */}
                <span 
                  className={`
                    flex items-center justify-center w-6 h-6 rounded text-xs font-bold
                    ${isActive 
                      ? 'border-ds-primary-100 text-ds-primary-700' 
                      : 'bg-bg-subtle text-text-secondary group-hover:bg-bg-muted'
                    }
                  `}
                  aria-hidden="true"
                >
                  {nature.monogram}
                </span>
                
                {/* Label */}
                <span className="whitespace-nowrap">
                  {nature.label}
                </span>
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default NatureTabs;
